import { useCallback, useState } from 'react';
import { type LayoutChangeEvent, Pressable, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import Animated, { useAnimatedStyle, withTiming } from 'react-native-reanimated';

import { colorSemantic, spacing, unisonColors, useAppTheme } from '@/theme';

export interface SlidingTabItem {
  readonly key: string;
  readonly label: string;
}

export type SlidingTabsVariant = 'page' | 'inline';

interface SlidingTabsProps {
  readonly items: readonly SlidingTabItem[];
  readonly activeKey: string;
  readonly onSelect: (key: string) => void;
  readonly variant?: SlidingTabsVariant;
  readonly testID?: string;
}

interface TabMeasurement {
  readonly x: number;
  readonly width: number;
}

const SLIDE_DURATION_MS = 200;
const PAGE_INDICATOR_HEIGHT = 3;
const INLINE_INDICATOR_HEIGHT = 2;

export const SlidingTabs = ({
  items,
  activeKey,
  onSelect,
  variant = 'page',
  testID = 'sliding-tabs',
}: SlidingTabsProps) => {
  const { colors } = useAppTheme();
  const [measurements, setMeasurements] = useState<readonly TabMeasurement[]>([]);

  const isPage = variant === 'page';
  const activeIndex = Math.max(
    items.findIndex((item) => item.key === activeKey),
    0,
  );
  const activeMeasurement = measurements[activeIndex];

  const handleTabLayout = useCallback((index: number, event: LayoutChangeEvent) => {
    const { width, x } = event.nativeEvent.layout;

    setMeasurements((current) => {
      const next = [...current];
      next[index] = { width, x };

      return next;
    });
  }, []);

  const indicatorStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: withTiming(activeMeasurement?.x ?? 0, { duration: SLIDE_DURATION_MS }) },
    ],
    width: withTiming(activeMeasurement?.width ?? 0, { duration: SLIDE_DURATION_MS }),
  }));

  return (
    <View
      style={[styles.container, isPage ? styles.containerPage : styles.containerInline]}
      testID={testID}>
      <View style={styles.tabRow}>
        {items.map((item, index) => {
          const isActive = item.key === activeKey;

          return (
            <Pressable
              key={item.key}
              accessibilityRole="tab"
              accessibilityState={{ selected: isActive }}
              style={isPage ? styles.tabPage : styles.tabInline}
              testID={`${testID}-${item.key}`}
              onLayout={(event) => handleTabLayout(index, event)}
              onPress={() => onSelect(item.key)}>
              <Text
                style={{ color: isActive ? colors.textNdPrimary : colors.textNdMuted }}
                variant={isPage ? 'titleMedium' : 'titleSmall'}>
                {item.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
      <View style={isPage ? styles.trackPage : styles.trackInline}>
        <Animated.View
          style={[isPage ? styles.indicatorPage : styles.indicatorInline, indicatorStyle]}
          testID={`${testID}-indicator`}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: unisonColors.white,
  },
  containerPage: {
    borderTopColor: colorSemantic.colorBorderNdBase,
    borderTopWidth: 1,
    elevation: 3,
    shadowColor: unisonColors.black,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 3,
  },
  containerInline: {
    backgroundColor: 'transparent',
  },
  tabRow: {
    flexDirection: 'row',
  },
  tabPage: {
    alignItems: 'center',
    flex: 1,
    paddingBottom: spacing[2],
    paddingTop: spacing[3],
  },
  tabInline: {
    paddingBottom: spacing[1],
    paddingRight: spacing[4],
  },
  trackPage: {
    height: PAGE_INDICATOR_HEIGHT,
  },
  trackInline: {
    borderBottomColor: unisonColors.borderNdBase,
    borderBottomWidth: StyleSheet.hairlineWidth,
    height: INLINE_INDICATOR_HEIGHT,
  },
  indicatorPage: {
    backgroundColor: colorSemantic.colorBorderBrandBase,
    height: PAGE_INDICATOR_HEIGHT,
  },
  indicatorInline: {
    backgroundColor: colorSemantic.colorBorderBrandBase,
    height: INLINE_INDICATOR_HEIGHT,
  },
});
